/** Synthetic category id when fileContent uses top-level `sections` only. */
export const FLAT_OUTLINE_CATEGORY_ID = 'wf-document-outline';

/** Normalize fileContent from API (object or JSON string). */
export function parseFileContent(raw) {
	if (!raw) return null;
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw);
		} catch {
			return null;
		}
	}
	return raw;
}

export function fileContentUsesCategories(fileContent) {
	return (
		Array.isArray(fileContent?.categories) &&
		fileContent.categories.length > 0
	);
}

export function getTopLevelSections(fileContent) {
	if (!fileContent) return [];
	if (fileContentUsesCategories(fileContent)) {
		return fileContent.categories.flatMap((c) => c.sections || []);
	}
	return Array.isArray(fileContent.sections) ? fileContent.sections : [];
}

function normalizeTitle(value) {
	return String(value || '')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim();
}

/** Match project TOC rules — also used to hide non-navigable sidebar items. */
export function shouldIncludeInSidebarNav(title, excludeFromToc) {
	if (excludeFromToc === true) return false;
	const t = normalizeTitle(title);
	if (!t) return false;
	if (t.includes('apa') && (t.includes('compliance') || t.includes('manual'))) {
		return false;
	}
	if (t === 'keywords' || t.startsWith('keywords:') || t.includes(' keyword')) {
		return false;
	}
	return true;
}

function parseHtmlFragment(html) {
	if (!html || typeof html !== 'string') return null;
	return new DOMParser().parseFromString(html, 'text/html');
}

function extractFirstH2Text(html) {
	if (!html || typeof html !== 'string') return null;
	const doc = parseHtmlFragment(html);
	if (!doc) return null;
	const h2 = doc.querySelector('h2');
	const text = (h2?.textContent || '').trim();
	return text || null;
}

function subsectionHasNavigableHeading(sub) {
	const html = sub?.content?.html || '';
	if (!html.trim()) return false;

	const doc = parseHtmlFragment(html);
	if (!doc) return false;

	const h2 = doc.querySelector('h2');
	if (!h2) return false;

	const title = normalizeTitle(sub.title);
	const h2Text = normalizeTitle(h2.textContent);
	if (!h2Text) return false;

	return (
		h2Text === title ||
		h2Text.includes(title) ||
		title.includes(h2Text)
	);
}

/** Virtual nav id for h2 headings embedded in a section block (e.g. professional paper intro). */
export function buildVirtualH2NavId(sectionId, h2Index) {
	return `${sectionId}__h2__${h2Index}`;
}

export function parseVirtualH2NavId(id) {
	const match = String(id || '').match(/^(section-\d+(?:-added-\d+)?)__h2__(\d+)$/);
	if (!match) return null;
	return { sectionId: match[1], h2Index: parseInt(match[2], 10) };
}

function extractH2NavItems(section) {
	const htmlParts = [section?.content?.html || ''];
	(section.subsections || []).forEach((sub) => {
		if (sub && sub.isVisible !== false) {
			htmlParts.push(sub?.content?.html || '');
		}
	});
	const html = htmlParts.join('\n');
	if (!html.trim()) return [];

	const doc = parseHtmlFragment(html);
	if (!doc) return [];

	const sectionTitle = normalizeTitle(section?.title);
	const h2s = Array.from(doc.querySelectorAll('h2'));

	return h2s
		.map((h2, idx) => {
			if (h2.classList?.contains('wf-level1-heading')) return null;
			const title = (h2.textContent || '').trim();
			if (!title) return null;
			const h2Norm = normalizeTitle(title);
			if (sectionTitle && (h2Norm === sectionTitle || h2Norm === `${sectionTitle}s`)) {
				return null;
			}
			return {
				id: buildVirtualH2NavId(section.id, idx),
				title,
				canDelete: false,
				isVirtualH2: true,
			};
		})
		.filter(Boolean);
}

function shouldUseH2NavForSection(section) {
	const h2Items = extractH2NavItems(section);
	if (!h2Items.length) return false;

	const navigableSubs = filterSubsectionsForSidebar(section.subsections || []);
	if (!navigableSubs.length) return true;
	return navigableSubs.length < h2Items.length;
}

function isGuidanceOnlySubsection(sub) {
	const title = normalizeTitle(sub?.title);
	const guidanceTitles = new Set([
		'transition paragraph',
		'transition to method',
		'transition into method',
		'transition to chapter 2',
		'closing paragraph',
	]);
	if (guidanceTitles.has(title)) return true;

	const html = sub?.content?.html || '';
	if (!html.trim()) return false;

	const doc = parseHtmlFragment(html);
	if (!doc) return false;

	const hasH2 = Boolean(doc.querySelector('h2'));
	const scaffoldOnly =
		Boolean(doc.querySelector('.wf-scaffold')) &&
		!hasH2 &&
		(doc.body?.textContent || '').trim().length > 0;

	return scaffoldOnly;
}

function filterSubsectionsForSidebar(subsections) {
	return (subsections || []).filter((sub) => {
		if (!sub || sub.isVisible === false) return false;
		if (sub.optional === true && sub.required === false) return false;
		if (!shouldIncludeInSidebarNav(sub.title, sub.excludeFromToc)) return false;
		if (isGuidanceOnlySubsection(sub)) return false;
		return subsectionHasNavigableHeading(sub);
	});
}

export function mapSectionForSidebar(section) {
	const h2NavItems = shouldUseH2NavForSection(section) ? extractH2NavItems(section) : [];
	const subsectionNavItems =
		h2NavItems.length > 0
			? h2NavItems
			: filterSubsectionsForSidebar(section.subsections).map((sub) => {
					const h2Title = extractFirstH2Text(sub?.content?.html || '');
					return {
						id: sub.id,
						title: h2Title || sub.title,
						canDelete:
							(sub.required === false && sub.id.includes('-added-')) ||
							sub.required === true,
					};
				});

	return {
		id: section.id,
		title: section.title,
		canDelete:
			(section.required === false && section.id.includes('-added-')) ||
			section.required === true,
		subsections: subsectionNavItems,
		optionalSubsections: (section.subsections || [])
			.filter(
				(sub) =>
					sub.required === false &&
					sub.optional === true &&
					sub.isVisible !== false
			)
			.map((sub) => ({
				id: sub.id,
				title: sub.title,
			})),
		deletedRequiredSubsections: (section.subsections || [])
			.filter((sub) => sub.required === true && sub.isVisible === false)
			.map((sub) => ({
				id: sub.id,
				title: sub.title,
			})),
	};
}

export function mapCategoryForSidebar(category) {
	return {
		id: category.id,
		name: category.name,
		sections: (category.sections || [])
			.filter(
				(section) =>
					section.isVisible !== false &&
					section.optional === false &&
					shouldIncludeInSidebarNav(section.title, section.excludeFromToc)
			)
			.map(mapSectionForSidebar),
		optionalSections: (category.sections || [])
			.filter(
				(section) =>
					section.required === false &&
					section.optional === true &&
					section.isVisible !== false
			)
			.map((section) => ({
				id: section.id,
				title: section.title,
			})),
		deletedRequiredSections: (category.sections || [])
			.filter(
				(section) =>
					section.required === true && section.isVisible === false
			)
			.map((section) => ({
				id: section.id,
				title: section.title,
			})),
	};
}

/** Build sidebar category list from fileContent (categories or flat sections). */
export function buildSidebarSectionsFromFileContent(fileContent, documentTitle = '') {
	if (!fileContent) return [];

	if (fileContentUsesCategories(fileContent)) {
		return fileContent.categories.map(mapCategoryForSidebar);
	}

	const sections = Array.isArray(fileContent.sections) ? fileContent.sections : [];
	if (!sections.length) return [];

	const visibleSections = sections.filter(
		(section) =>
			section.isVisible !== false &&
			(section.optional === false || section.optional === undefined) &&
			shouldIncludeInSidebarNav(section.title, section.excludeFromToc)
	);

	return [
		{
			id: FLAT_OUTLINE_CATEGORY_ID,
			name: documentTitle || 'Sections',
			sections: visibleSections.map(mapSectionForSidebar),
			optionalSections: sections
				.filter(
					(section) =>
						section.required === false &&
						section.optional === true &&
						section.isVisible !== false
				)
				.map((section) => ({
					id: section.id,
					title: section.title,
				})),
			deletedRequiredSections: sections
				.filter(
					(section) =>
						section.required === true && section.isVisible === false
				)
				.map((section) => ({
					id: section.id,
					title: section.title,
				})),
		},
	];
}

/** Resolve sidebar highlight id from cursor node (DOM section/subsection ids). */
export function resolveOutlineIdFromNode(node, root) {
	let el = node;
	while (el && el !== root) {
		const id = el.id || '';
		if (id.startsWith('section-') || id.startsWith('subsection-')) {
			return id;
		}
		el = el.parentElement;
	}
	return null;
}

/** Flatten sidebar nav items in display order (subsections or section-level entries). */
export function flattenSidebarNavItems(sidebarSections) {
	const items = [];
	if (!Array.isArray(sidebarSections) || !sidebarSections.length) return items;

	const isCategories =
		sidebarSections[0]?.sections !== undefined && sidebarSections[0]?.name !== undefined;

	const pushSection = (section) => {
		if (!section?.id) return;
		const subs = section.subsections || [];
		if (subs.length > 0) {
			subs.forEach((sub) => {
				if (sub?.id) items.push({ id: sub.id, parentSectionId: section.id });
			});
		} else {
			items.push({ id: section.id, parentSectionId: section.id });
		}
	};

	if (isCategories) {
		sidebarSections.forEach((category) => {
			(category.sections || []).forEach(pushSection);
		});
	} else {
		sidebarSections.forEach(pushSection);
	}

	return items;
}

/** Map a sidebar nav id to its scroll anchor element in the editor body. */
export function resolveDomElementForNavId(body, navId, sectionTitle = '') {
	if (!body || !navId) return null;

	const virtual = parseVirtualH2NavId(navId);
	if (virtual) {
		const section = body.querySelector(`#${CSS.escape(virtual.sectionId)}`);
		if (!section) return null;
		const h2List = Array.from(section.querySelectorAll('h2'));
		return h2List[virtual.h2Index] || section;
	}

	const el = body.querySelector(`#${CSS.escape(navId)}`);
	if (!el) return null;

	if (navId.startsWith('subsection-')) {
		return el.querySelector('h2, h3, h4') || el;
	}

	if (navId.startsWith('section-')) {
		return el.querySelector('h1, h2') || el;
	}

	return el;
}

function getSectionTitleForNavId(sidebarSections, navId) {
	if (!navId || !Array.isArray(sidebarSections)) return '';

	const virtual = parseVirtualH2NavId(navId);
	const isCategories =
		sidebarSections.length > 0 &&
		sidebarSections[0]?.sections !== undefined &&
		sidebarSections[0]?.name !== undefined;

	const sectionsList = isCategories
		? sidebarSections.flatMap((c) => c.sections || [])
		: sidebarSections;

	for (const section of sectionsList) {
		if (!section) continue;
		if (section.id === navId || virtual?.sectionId === section.id) {
			return section.title || '';
		}
		for (const sub of section.subsections || []) {
			if (sub?.id === navId) return section.title || '';
		}
	}
	return '';
}

/** Collect sidebar nav anchors in document order for scroll/cursor sync. */
export function collectNavAnchorsFromSidebar(body, sidebarSections) {
	const flat = flattenSidebarNavItems(sidebarSections);
	return flat
		.map(({ id, parentSectionId }) => {
			const sectionTitle = getSectionTitleForNavId(sidebarSections, id || parentSectionId);
			const el = resolveDomElementForNavId(body, id, sectionTitle);
			return el ? { id, el } : null;
		})
		.filter(Boolean);
}

function pickActiveAnchorByViewport(anchors, referenceTop, offsetPx = 120) {
	if (!anchors.length) return null;

	const anchorLine = referenceTop + offsetPx;
	let activeId = anchors[0].id;

	for (const anchor of anchors) {
		const rect = anchor.el.getBoundingClientRect();
		if (rect.top <= anchorLine + 8) {
			activeId = anchor.id;
		} else {
			break;
		}
	}

	return activeId;
}

/**
 * Resolve active nav id from cursor/selection position.
 * Prefers the deepest sidebar anchor that contains the node.
 */
export function resolveActiveOutlineIdFromElement(node, body, sidebarSections) {
	if (!node || !body) return null;

	const anchors = collectNavAnchorsFromSidebar(body, sidebarSections);
	if (!anchors.length) return resolveOutlineIdFromNode(node, body);

	let best = null;
	for (const anchor of anchors) {
		if (anchor.el === node || anchor.el.contains(node)) {
			best = anchor.id;
		}
	}

	if (best) return best;

	// Subsection wrapper may contain the node while the anchor is the inner h2
	const subsectionEl = node.closest?.('[id^="subsection-"]');
	if (subsectionEl?.id) {
		const match = anchors.find((a) => {
			const sub = a.el.closest?.('[id^="subsection-"]');
			return sub?.id === subsectionEl.id || a.id === subsectionEl.id;
		});
		if (match) return match.id;
	}

	const nodeRect = node.getBoundingClientRect?.();
	if (nodeRect) {
		return pickActiveAnchorByViewport(anchors, 0, nodeRect.top);
	}

	return resolveOutlineIdFromNode(node, body);
}

/**
 * Resolve active nav id from editor scroll position (Google Docs–style outline sync).
 * Uses viewport coordinates so it works regardless of which TinyMCE container scrolls.
 */
export function resolveActiveOutlineIdFromScroll(editor, sidebarSections, offsetPx = 120) {
	if (!editor) return null;
	const body = editor.getBody();
	if (!body) return null;

	const anchors = collectNavAnchorsFromSidebar(body, sidebarSections);
	if (!anchors.length) return null;

	const iframe = editor.getContentAreaContainer?.()?.querySelector('iframe');
	const iframeRect = iframe?.getBoundingClientRect?.();
	const referenceTop = iframeRect?.top ?? 0;

	return pickActiveAnchorByViewport(anchors, referenceTop, offsetPx);
}

/** Scrollable targets inside and around the TinyMCE editor (iframe + parent panes). */
export function getEditorScrollTargets(editor) {
	const targets = new Set();
	const win = editor.getWin?.();
	const doc = editor.getDoc?.();
	if (win) targets.add(win);
	if (doc?.documentElement) targets.add(doc.documentElement);
	if (doc?.body) targets.add(doc.body);

	const container = editor.getContainer?.();
	if (container) {
		container.querySelectorAll('.tox-edit-area, .tox-sidebar-wrap').forEach((el) => {
			targets.add(el);
		});

		const iframe = container.querySelector('iframe');
		let node = iframe?.parentElement;
		while (node && node !== document.documentElement) {
			const style = window.getComputedStyle(node);
			if (/(auto|scroll|overlay)/.test(style.overflowY)) {
				targets.add(node);
			}
			node = node.parentElement;
		}
	}

	return [...targets];
}

/** Find sidebar category name that contains a nav id (for auto-expand). */
export function findSidebarCategoryForNavId(sidebarSections, navId) {
	if (!navId || !Array.isArray(sidebarSections)) return null;

	const isCategories =
		sidebarSections.length > 0 &&
		sidebarSections[0]?.sections !== undefined &&
		sidebarSections[0]?.name !== undefined;

	if (!isCategories) return sidebarSections[0]?.name || null;

	for (const category of sidebarSections) {
		for (const section of category.sections || []) {
			if (section.id === navId) return category.name;
			for (const sub of section.subsections || []) {
				if (sub.id === navId) return category.name;
			}
			const virtual = parseVirtualH2NavId(navId);
			if (virtual && section.id === virtual.sectionId) return category.name;
		}
	}
	return null;
}
